import { useEffect, useState } from 'react'
import { API_URL } from '../../util'
import {
  SectionTitle,
  SectionTitleSize,
  SectionTitleStyle
} from '../SectionTitle/SectionTitle'

// Backlog #32: public Contract-Value charting block. Fetches each contract's
// year-to-date invoiced value against its target and draws a horizontal bar per
// contract (invoiced as a proportion of target), coloured by how close it is.
type ContractValueRow = {
  name: string
  target: number
  invoiced: number
  percentage: number
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0
  }).format(value)
}

function barColour(percentage: number): string {
  if (percentage >= 100) return '#2e7d32'
  if (percentage >= 85) return '#f9a825'
  return '#c8102e'
}

function ContractValueChart(props: { title?: string }) {
  const [rows, setRows] = useState<ContractValueRow[] | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let active = true
    fetch(`${API_URL}contractValueChart`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => active && setRows(d.contracts ?? []))
      .catch(() => active && setError(true))
    return () => {
      active = false
    }
  }, [])

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', width: '100%' }}>
      <SectionTitle
        title={props.title || 'Contract Value — Invoiced vs Target'}
        size={SectionTitleSize.Medium}
        style={SectionTitleStyle.Dark}
      />
      {error ? (
        <p>Contract value data is unavailable right now.</p>
      ) : rows === null ? (
        <p>Loading…</p>
      ) : rows.length === 0 ? (
        <p>No contracts with a contract value are being tracked yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {rows.map((row, i) => {
            const pct = Math.max(2, Math.min(100, row.percentage))
            return (
              <div key={i}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 14,
                    marginBottom: 4
                  }}
                >
                  <span style={{ fontWeight: 600, color: '#0c2340' }}>
                    {row.name}
                  </span>
                  <span style={{ color: '#5b6472' }}>
                    {formatCurrency(row.invoiced)} of {formatCurrency(row.target)}{' '}
                    ({row.percentage}%)
                  </span>
                </div>
                <div
                  style={{
                    background: '#eef1f5',
                    borderRadius: 4,
                    height: 18,
                    overflow: 'hidden'
                  }}
                >
                  <div
                    style={{
                      width: `${pct}%`,
                      height: '100%',
                      background: barColour(row.percentage)
                    }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export { ContractValueChart }
